import type { ChangeEvent, SyntheticEvent } from 'react';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { ApolloError } from '@apollo/client';
import { storeKeyMapper, upperCasePromoCode } from '@shared/utils/src/ct';
import type { Cart } from '../../../codegen/types';
import { useCartContext } from '../../../context/cartContext';
import { useCheckoutConfirmationContext } from '../../../context/checkoutConfirmationContext';
import { useLocaleContext } from '../../../context/localeContext';
import { useApplyDiscountMutation } from '../../../graphqlDocument/cart/applyDiscount.generated';
import { useRemoveDiscountMutation } from '../../../graphqlDocument/cart/removeDiscount.generated';
import { useNRBASession } from '../../../hooks/useNRBASession';
import { CheckoutCloseSmall, PriceTag } from '../../../icons';
import type { BffError } from '../../../services/errors';
import { getAmount } from '../../../utils/currencyHelper';
import { sendAddCouponEvent } from '../../../utils/gtm/events/add_coupon/sendAddCouponEvent';
import { Locale, getCtLocale } from '../../../utils/internationalization';
import { transformTextToId } from '../../../utils/transformers';
import {
  AddButton,
  ButtonContainer,
  Container,
  ErrorMessage,
  FormContainer,
  Lozenge,
  LozengeContainer,
  NotificationMessage,
  OrderSummaryTitle,
  PromoBoxForm,
  PromoInput,
  PromoName,
  RemoveButton,
  Title,
  TitleContainer,
} from './PromoBox.styles';

enum DiscountCodeError {
  DUPLICATE_PROMO_ERROR = 'DiscountCodeErrorDuplicatedPromoCode',
  PROMO_LIMIT_EXCEEDED = 'DiscountCodeErrorPromoCodeLimitExceeded',
  DUPLICATE_CART_PROMOTION = 'DiscountCodeErrorDuplicatedPromotion',
  DISCOUNT_GENERAL_ERROR = 'DiscountCodeErrorDiscountCodeNonApplicable',
  DISCOUNT_CART_HAS_GIFT_CARD = 'DiscountCodeErrorCartHasGiftCard',
}

export function PromoBox({ isStar2 = false }: { isStar2?: boolean }) {
  const { t } = useTranslation(['lib-global-common']);
  const router = useRouter();
  const { cartId, anonymousId, miniCart, setMiniCart, setShouldUpdateDeliveryOptions } = useCartContext();
  const { orderData } = useCheckoutConfirmationContext();
  const { discountCodes } = { ...miniCart };
  const { locale } = useLocaleContext();
  const [promoCode, setPromoCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const currentLocale = locale ?? Locale.DEFAULT_LOCALE;
  const storeKey = storeKeyMapper(currentLocale);
  const nrbaContextHeader = useNRBASession();
  const [applyDiscountMutation, { loading: applyIsLoading }] = useApplyDiscountMutation(nrbaContextHeader);
  const [removeDiscountMutation] = useRemoveDiscountMutation(nrbaContextHeader);
  const isConfirmationPage = router.asPath === '/checkout/confirmation';
  const isConfirmationPageDiscountCodes = isConfirmationPage ? orderData?.getOrderedCart.discountCodes : discountCodes;
  const promoTitle = t('cart.promo.title');

  const handleInputPromo = (event: ChangeEvent<HTMLInputElement>) => setPromoCode(event.target.value);

  const applyDiscount = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (promoCode.trim() === '') {
      setErrorMessage(t('cart.promo.error.message.generic'));
      return;
    }

    const input = {
      cartId,
      anonymousId,
      storeKey,
      locale: getCtLocale(currentLocale),
      discountCode: upperCasePromoCode(promoCode),
    };

    try {
      const { data } = await applyDiscountMutation({
        variables: { input },
        onCompleted: ({ applyDiscount: applyDiscountData }) => {
          setMiniCart(applyDiscountData as Cart);
          setShouldUpdateDeliveryOptions(true);
          setPromoCode('');
        },
      });

      if (data) {
        if (errorMessage) setErrorMessage('');

        const coupons = data.applyDiscount?.discountCodes
          ?.map((code) => code?.discountCode || code?.discountName)
          .join(', ');

        if (!coupons) return;

        sendAddCouponEvent(
          { coupon: coupons, coupon_value: +getAmount(data.applyDiscount?.discountTotal) },
          data.applyDiscount.id,
        );

        if (isStar2) {
          setNotificationMessage(t('cart.promo.notification.message.generic'));
          router.reload();
        }
      }
    } catch (e) {
      const error = e as ApolloError;
      const bffError = error.graphQLErrors?.[0] as unknown as BffError;
      const bffErrorInfo = bffError?.errorInfo?.[0];
      if (notificationMessage) setNotificationMessage('');

      switch (bffError.errorType) {
        case DiscountCodeError.DUPLICATE_PROMO_ERROR:
          setErrorMessage(t('cart.promo.error.message.duplicate'));
          break;
        case DiscountCodeError.PROMO_LIMIT_EXCEEDED:
          setErrorMessage(t('cart.promo.error.message.limit.exceeded'));
          break;
        case DiscountCodeError.DUPLICATE_CART_PROMOTION:
          setErrorMessage(t('cart.promo.error.message.promo.duplicate', { discountName: bffErrorInfo?.code }));
          break;
        case DiscountCodeError.DISCOUNT_CART_HAS_GIFT_CARD:
          setErrorMessage(t('cart.promo.error.has.gift.card'));
          break;
        default:
          setErrorMessage(t('cart.promo.error.message.generic'));
          break;
      }
      console.error(error);
    }
  };

  const removeDiscount = (discountId: string) => async () => {
    const input = {
      cartId,
      anonymousId,
      storeKey,
      locale: getCtLocale(currentLocale),
      discountId,
    };
    try {
      await removeDiscountMutation({
        variables: { input },
        onCompleted: ({ removeDiscount: removeDiscountData }) => {
          setMiniCart(removeDiscountData as Cart);
          setShouldUpdateDeliveryOptions(true);
          if (isStar2) {
            router.reload();
          }
        },
      });
      if (notificationMessage) setNotificationMessage('');
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Container data-testid="promoBoxContainer">
      {!isConfirmationPage ? (
        <>
          <TitleContainer>
            <ButtonContainer
              type="button"
              data-testid="openPromoboxButton"
              aria-label={promoTitle}
              aria-controls={`promo-box-${transformTextToId(promoTitle)}`}
            >
              <Title>{promoTitle}</Title>
            </ButtonContainer>
          </TitleContainer>
          <FormContainer id={`promo-box-${transformTextToId(promoTitle)}`}>
            <PromoBoxForm onSubmit={applyDiscount} method="post">
              <PromoInput
                placeholder={t('cart.promo.input.placeholder')}
                aria-label={t('cart.promo.input.placeholder')}
                type="text"
                maxLength={163}
                value={promoCode}
                onChange={handleInputPromo}
                $isError={!!errorMessage}
                data-testid="promoInput"
                onBlur={(e) => {
                  e.target.value = promoCode;
                }}
              />
              <AddButton type="submit" disabled={applyIsLoading} data-testid="addPromoButton">
                {t('cart.promo.add')}
              </AddButton>
            </PromoBoxForm>
            <ErrorMessage role="alert" data-testid="promoErrorMessage">
              {errorMessage}
            </ErrorMessage>
          </FormContainer>
        </>
      ) : null}

      {isConfirmationPage ? <OrderSummaryTitle>{t('promo.box.order.summary')}</OrderSummaryTitle> : null}

      {!!isConfirmationPageDiscountCodes?.length && (
        <LozengeContainer>
          {isConfirmationPageDiscountCodes?.map((code) => {
            const { discountCode, discountId } = { ...code };
            return (
              <Lozenge key={discountId} data-testid="promoLozenge">
                <PriceTag />
                <PromoName data-testid="promoName">{discountCode}</PromoName>
                {!isConfirmationPage ? (
                  <RemoveButton
                    aria-label={t('cart.aria.remove.discount', { discountName: discountCode })}
                    type="button"
                    onClick={removeDiscount(discountId as string)}
                    data-testid="removePromoButton"
                  >
                    <CheckoutCloseSmall />
                  </RemoveButton>
                ) : null}
              </Lozenge>
            );
          })}
        </LozengeContainer>
      )}
      <NotificationMessage role="alert">
        {isStar2 && discountCodes?.length ? t('cart.promo.notification.message.generic') : null}
      </NotificationMessage>
    </Container>
  );
}
